import React from 'react';

type CardElement = 'div' | 'article' | 'section' | 'header';

interface CardProps extends Omit<React.HTMLAttributes<HTMLElement>, 'className'> {
  children: React.ReactNode;
  className?: string;
  /** Accent border + shadow lift on hover. Use for metric tiles and clickable summary cards. */
  variant?: 'default' | 'interactive';
  /**
   * @deprecated Use variant="interactive"
   */
  hover?: boolean;
  as?: CardElement;
}

interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface CardBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const Card: React.FC<CardProps> & {
  Header: React.FC<CardHeaderProps>;
  Body: React.FC<CardBodyProps>;
  Footer: React.FC<CardFooterProps>;
} = ({
  children,
  className = '',
  variant = 'default',
  hover = false,
  as: Component = 'div',
  onClick,
  ...rest
}) => {
  const isInteractive = variant === 'interactive' || hover;
  const classes = [
    'ui-card',
    isInteractive && 'ui-card--interactive',
    (isInteractive || onClick) && 'cursor-pointer',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={classes} onClick={onClick} {...rest}>
      {children}
    </Component>
  );
};

const CardHeader: React.FC<CardHeaderProps> = ({ children, className = '' }) => {
  return <div className={`px-6 py-4 border-b border-border ${className}`}>{children}</div>;
};

const CardBody: React.FC<CardBodyProps> = ({ children, className = '' }) => {
  return <div className={`p-2 ${className}`}>{children}</div>;
};

const CardFooter: React.FC<CardFooterProps> = ({ children, className = '' }) => {
  return <div className={`px-6 py-4 border-t border-border ${className}`}>{children}</div>;
};

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
